/* Reading a company profile. */
export const LISTING = "listing";
export const VERIFIED = "verified";
export const CLAIMED = "claimed";
export const WARNING = "warning";

export const profileStatus = (company) => {
  if (company?.companyID === undefined) return LISTING;
  if (company?.verifiedCompany != null) return VERIFIED;
  return (company?.claimed && CLAIMED) || (company?.draft && WARNING) || false;
};

export const MEMBER = "member";

/* The membership-aware ladder (TIP-505), as distinct from the legacy chain
 * above.
 *
 * `profileStatus` predates Membership and CANNOT answer "member" — it only
 * knows verified / claimed / warning / listing. The live banner therefore
 * computes a second value, `statusData`, from the backend's own
 * `profile_status`, falling back to the legacy chain when the field is absent.
 *
 * The difference is load-bearing twice over: EnterpriseAddCompany branches on
 * it, and it is the whole distinction between offering an owner "Get Verified"
 * (they are a member) and "Get Membership" (they are not).
 *
 * It lives here rather than in usePurchaseStatus so that a component needing
 * the status does not have to import a hook module to get it.
 */
export const statusLadder = (company) => {
  const s = company?.profile_status;
  if (s === "verified") return VERIFIED;
  if (s === MEMBER) return MEMBER;
  if (s === "claimed") return CLAIMED;
  if (s === "listed") return LISTING;
  return profileStatus(company);
};

/* A TREVEX record (TRX…) carries its own nested address and industry rows; a
   WorldBox record answers from a flat `company` object. Transcribed: the live
   screen decides this from the id in the URL, which is what `trevexId` is. */
export const addressFields = (company, trevexId) => {
  /* The live code writes `company?.companyAddress[0]?…`, which throws when a
     company has no companyAddress array at all. Guarded; the loaded case is
     identical. */
  const trx = company?.companyAddress?.[0]?.company_address_detailsID;
  const wb = company?.company;

  if (trevexId) {
    return {
      state: trx?.state,
      city: trx?.city,
      area: trx?.area,
      zip: trx?.postal_code,
      addressLine1: trx?.address_line_1,
      email: trx?.email,
      phone: trx?.phone,
      /* Transcribed formatting: the pipe separators the API stores are
         stripped, then a 10-digit number is grouped 3-3-4. */
      telephone: formatTelephone(trx?.telephone),
      workingHours: trx?.working_hours,
    };
  }

  /* Transcribed: a WorldBox record only ever shows these three, and its ZIP
     is suppressed when it is an empty string rather than absent. */
  return {
    state: wb?.city,
    city: wb?.city,
    zip: wb?.postcode !== "" ? wb?.postcode : undefined,
  };
};

/* Transcribed verbatim from GeneralInfo. */
export const formatTelephone = (value) => {
  if (!value) return undefined;
  return String(value)
    .replace(/\|/g, "")
    .replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3");
};

/* The industry chips. Transcribed: the general-information tab reads
   `sub_industry_data`, which is its own list on the company — not the
   `industry` relation the search cards read. */
export const industryActivities = (company) =>
  (company?.sub_industry_data || [])
    .map((row) => ({ id: row?.id, label: row?.sub_industry }))
    .filter((row) => row.label);
